import { useState, useEffect } from "react";
import { X, Plus, Trash2, Save, DollarSign } from "lucide-react";
import { profitTrackingService } from "../../../../services/profitTrackingService";
import { ExpenseItem } from "../../../../services/profitTrackingService";

interface ExpenseModalProps {
  profitTrackingId: string;
  date: string;
  onClose: () => void;
  onSaved: () => void;
}

interface ExpenseFormRow {
  tempId: string;
  description: string;
  amount: string;
  currency: string;
}

export default function ExpenseModal({ profitTrackingId, date, onClose, onSaved }: ExpenseModalProps) {
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [rows, setRows] = useState<ExpenseFormRow[]>([{ tempId: "1", description: "", amount: "", currency: "XAF" }]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    profitTrackingService
      .listExpenses(profitTrackingId)
      .then((res) => {
        if (res?.data) setExpenses(res.data);
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [profitTrackingId]);

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      { tempId: Math.random().toString(36).slice(2), description: "", amount: "", currency: "XAF" },
    ]);
  };

  const removeRow = (tempId: string) => {
    setRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const updateRow = (tempId: string, field: keyof ExpenseFormRow, value: string) => {
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, [field]: value } : r)));
  };

  const handleSubmit = async () => {
    const validRows = rows.filter((r) => r.description.trim() && parseFloat(r.amount) > 0);
    if (validRows.length === 0) return;

    setLoading(true);
    try {
      for (const row of validRows) {
        await profitTrackingService.createExpense({
          profit_tracking_id: profitTrackingId,
          description: row.description.trim(),
          amount: parseFloat(row.amount),
          currency: row.currency,
        });
      }
      const res = await profitTrackingService.listExpenses(profitTrackingId);
      if (res?.data) setExpenses(res.data);
      setRows([{ tempId: Math.random().toString(36).slice(2), description: "", amount: "", currency: "XAF" }]);
      onSaved();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpense = async (expenseId: string) => {
    if (!confirm("Supprimer cette dépense ?")) return;
    try {
      await profitTrackingService.deleteExpense(expenseId);
      setExpenses((prev) => prev.filter((e) => e.id !== expenseId));
      onSaved();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Dépenses</h2>
            <p className="text-sm text-slate-500">Date: {date}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Existing expenses */}
          {expenses.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">Dépenses enregistrées</h3>
              <div className="space-y-2">
                {expenses.map((exp) => (
                  <div
                    key={exp.id}
                    className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{exp.description}</p>
                      <p className="text-xs text-slate-500">
                        {exp.amount.toLocaleString("fr-FR")} {exp.currency}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteExpense(exp.id)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add new expenses */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Ajouter de nouvelles dépenses</h3>
            <div className="space-y-2">
              {rows.map((row) => (
                <div key={row.tempId} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Description"
                    value={row.description}
                    onChange={(e) => updateRow(row.tempId, "description", e.target.value)}
                    className="flex-1 min-w-0 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Montant"
                    value={row.amount}
                    onChange={(e) => updateRow(row.tempId, "amount", e.target.value)}
                    className="w-28 border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={row.currency}
                    onChange={(e) => updateRow(row.tempId, "currency", e.target.value)}
                    className="w-20 border border-slate-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="XAF">XAF</option>
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                  <button onClick={() => removeRow(row.tempId)} className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={addRow} className="mt-2 flex items-center text-sm text-blue-600 hover:text-blue-800">
              <Plus className="w-4 h-4 mr-1" />
              Ajouter une ligne
            </button>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
          >
            Fermer
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || rows.filter((r) => r.description.trim() && parseFloat(r.amount) > 0).length === 0}
            className="flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? (
              "Enregistrement..."
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Enregistrer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
